import { Colors } from '../../common/config/constants'

const CLUSTER_MAX_ZOOM = 13
const CLUSTER_RADIUS = 42

type EventFunction = (ev?: any) => void

const POINTS_FILTER = ['!has', 'point_count']

class OOHClusterLayer {
  unclusteredSourceId = 'geojson-unclustered-source'
  clusterSourceId = 'geojson-cluster-source'
  clusterLayerId = 'geojson-cluster-layer'
  labelsLayerId = 'geojson-labels-layer'
  pointsLayerId = 'geojson-points-layer'

  keyIndicator = 'nvisitors'

  sources = [
    {
      id: this.clusterSourceId,
      type: 'geojson',
      cluster: true,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: CLUSTER_RADIUS,
      data: null,
      clusterProperties: {
        [this.keyIndicator]: ['+', ['get', this.keyIndicator]],
        [this.keyIndicator + '_other']: [
          '+',
          ['get', this.keyIndicator + '_other'],
        ],
        id: ['min', ['get', 'id']],
      },
    },
    {
      id: this.unclusteredSourceId,
      type: 'geojson',
      cluster: false,
      data: null,
    },
  ]
  layers = [
    {
      id: this.clusterLayerId,
      source: this.clusterSourceId,
      type: 'circle',
      filter: ['has', 'point_count'],
      paint: {
        'circle-blur': 0.1,
        'circle-stroke-width': 2,
        'circle-stroke-color': Colors.colorAccent,
        'circle-color': '#FFF',
        'circle-radius': ['+', 10, ['sqrt', ['get', 'point_count']]],
      },
    },
    {
      id: this.labelsLayerId,
      source: this.clusterSourceId,
      type: 'symbol',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Regular'],
        'text-size': 14,
      },
    },
    {
      id: this.pointsLayerId,
      source: this.clusterSourceId,
      type: 'symbol',
      filter: POINTS_FILTER,
      layout: {
        'icon-image': 'marker',
        'icon-size': 1,
        'icon-allow-overlap': true,
      },
    },
  ]

  map = null
  _cachedClusterData = null

  useCluster = true

  onClick: EventFunction = null
  onMouseEnter: EventFunction = null
  onMouseOut: EventFunction = null
  _hoveredFeatureId: string | number = null

  constructor({ onClick, onMouseEnter, onMouseOut }) {
    this.onClick = onClick
    this.onMouseEnter = onMouseEnter
    this.onMouseOut = onMouseOut
  }

  init(map) {
    this.map = map
    this.sources.forEach((s) => {
      const { id, ...source } = s
      this.map.addSource(id, source)
    })
    this.layers.forEach((l) => {
      this.map.addLayer({
        ...l,
        layout: l.layout || {},
        paint: l.paint || {},
      })
    })

    this.initInteractivity()
  }

  initInteractivity() {
    this.layers.forEach((layer) => {
      this.map.on('click', layer.id, this.onClick)
      this.map.on('mousemove', layer.id, this._onMouseMove)
      this.map.on('mouseleave', layer.id, this._onMouseLeave)
    })
  }

  // mousemove + mouseleave are Mapbox GL layer events (not DOM events),
  // so DOM bubbling/delegation concerns do not apply.
  // mousemove is used instead of mouseenter because the labels layer
  // sits on top of the cluster circles and intercepts mouseenter events.
  _onMouseMove = (ev) => {
    if (ev.features.length) {
      const feat = ev.features[0]
      const featId = feat.properties?.id ?? feat.id
      if (featId === this._hoveredFeatureId) {
        return
      }
      this._hoveredFeatureId = featId
      const mapCanvas = this.map.getCanvas()
      mapCanvas.style.cursor = 'pointer'
      this.onMouseEnter(ev)
    }
  }

  _onMouseLeave = () => {
    this._hoveredFeatureId = null
    const mapCanvas = this.map.getCanvas()
    mapCanvas.style.cursor = ''
    this.onMouseOut()
  }

  removeInteractivity() {
    this.layers.forEach((layer) => {
      this.map.off('click', layer.id, this.onClick)
      this.map.off('mousemove', layer.id, this._onMouseMove)
      this.map.off('mouseleave', layer.id, this._onMouseLeave)
    })
  }

  destroy() {
    if (!this.map) {
      return
    }

    this.removeInteractivity()

    this.layers.forEach((layer) => {
      if (this.map.getLayer(layer.id)) {
        this.map.removeLayer(layer.id)
      }
    })

    this.sources.forEach((source) => {
      if (this.map.getSource(source.id)) {
        this.map.removeSource(source.id)
      }
    })

    delete this.map
  }

  show() {
    if (this.map) {
      this.layers.forEach((layer) => {
        this.map.setLayoutProperty(layer.id, 'visibility', 'visible')
      })
    }
  }

  hide() {
    if (this.map) {
      this.layers.forEach((layer) => {
        this.map.setLayoutProperty(layer.id, 'visibility', 'none')
      })
    }
  }

  loadData(pois: any[], cluster = true) {
    const geojson = {
      type: 'FeatureCollection',
      features: pois.map((p) => ({
        type: 'Feature',
        properties: p,
        geometry: {
          type: 'Point',
          coordinates: [p.lng, p.lat],
        },
      })),
    }
    this.setSourceData({ data: geojson, cluster })
  }

  setSourceData({ data, cluster = true }) {
    if (cluster) {
      this._cachedClusterData = data
    }

    const sourceIndex = cluster ? 0 : 1
    const source = this.map.getSource(this.sources[sourceIndex].id)
    if (source) {
      source.setData(data)
    }

    if (this.useCluster !== cluster) {
      this.useCluster = cluster
      this.recreateLayers()
    }
  }

  recreateLayers() {
    const pointsLayerId = this.layers[2].id
    const oldLayers = this.map.getStyle().layers

    const source = this.useCluster
      ? this.clusterSourceId
      : this.unclusteredSourceId

    this.layers.forEach((layer) => {
      const layerDef = oldLayers.find((l) => l.id === layer.id) || layer

      if (this.map.getLayer(layer.id)) {
        this.map.removeLayer(layer.id)
      }

      if (this.useCluster || layer.id === pointsLayerId) {
        this.map.addLayer({ ...layerDef, source })
      }
    })
  }

  updateClusterMaxZoom(hasSelection: boolean) {
    if (!this.map) {
      return
    }

    const maxZoom = hasSelection ? 0 : CLUSTER_MAX_ZOOM
    this.sources[0].clusterMaxZoom = maxZoom

    // Restore cluster mode when deselecting
    if (!hasSelection) {
      this.useCluster = true
    }

    if (!this.map.getSource(this.clusterSourceId)) {
      return
    }

    this.removeInteractivity()

    this.layers.forEach((layer) => {
      if (this.map.getLayer(layer.id)) {
        this.map.removeLayer(layer.id)
      }
    })

    this.map.removeSource(this.clusterSourceId)

    const { id, ...sourceOpts } = this.sources[0]
    this.map.addSource(id, { ...sourceOpts, data: this._cachedClusterData })

    this.layers.forEach((l) => {
      if (this.useCluster || l.id === this.pointsLayerId) {
        this.map.addLayer({
          ...l,
          layout: l.layout || {},
          paint: l.paint || {},
        })
      }
    })

    this.initInteractivity()
  }

  setPointsStyle(style) {
    for (const key in style) {
      if (style[key]) {
        this.map.setLayoutProperty(this.layers[2].id, key, style[key])
      }
    }
  }

  setPointsFilter(filter) {
    const filterJoin = filter ? ['all', filter, POINTS_FILTER] : POINTS_FILTER
    this.map.setFilter(this.layers[2].id, filterJoin)
  }
}

export default OOHClusterLayer
